import { useEffect, useMemo, useRef, useState } from 'react';
import {
  visitEntities,
  BUCKET_VALUE_KINDS,
  type BucketDocument,
  type BucketEntity,
  type BucketProperty,
  type BucketValueKind
} from '@oysterjs/smartfill-contracts';
import {
  childrenWithOrdinals,
  entityDisplayLabel,
  humanizeName,
  type PropertyDraft,
  type SectionDraft
} from '../buckets/document';
import { pluralize } from '../sidebar/presentation';
import { useSessionMemory } from '../sidebar/session-memory';
import {
  CheckIcon,
  ChevronRightIcon,
  CopyIcon,
  PasteIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon
} from './Icons';

interface BucketTreeEditHandlers {
  onEditProperty: (draft: PropertyDraft | null) => void;
  onEditSection: (draft: SectionDraft | null) => void;
  onSaveProperty: () => void;
  onCancelProperty: () => void;
  onRemoveProperty: () => void;
  onSaveSection: () => void;
  onCancelSection: () => void;
  onRemoveEntity: (entityId: string) => void;
}

interface BucketTreeCommonProps {
  document: BucketDocument;
  /**
   * Sections at this depth or shallower start expanded; deeper collapse.
   * Defaults to 0: only the root section is open until the user drills in.
   */
  defaultExpandedDepth?: number;
  /** Heading for the root section; null hides it (browse under own title). */
  rootHeading?: string | null;
  /**
   * Controlled root collapse for hosts that render their own root header
   * (rootHeading null): hides the root's own fields while the section
   * outline stays visible, mirroring in-tree collapse semantics.
   */
  rootCollapsed?: boolean;
  /**
   * Remembers expand/collapse across unmounts under this key (the form page
   * swaps to a loading view between continuous-fill pages). Memory lives for
   * the sidebar's lifetime; omit it for ephemeral trees.
   */
  stateKey?: string;
}

interface BucketTreeEditProps {
  mode: 'edit';
  busy?: boolean;
  propertyDraft?: PropertyDraft | null;
  sectionDraft?: SectionDraft | null;
  /** Null when the viewer cannot edit this bucket: rows render read-only. */
  edit: BucketTreeEditHandlers | null;
}

interface BucketTreeBrowseProps {
  mode: 'browse';
  /** Copies one property value; resolves true on success. */
  onCopyValue: (property: BucketProperty) => Promise<boolean>;
  /** Pastes one section's subtree into the current page. */
  onPasteSection?: (entity: BucketEntity, heading: string) => void;
  /** Disables every section paste button (a paste is already running). */
  pasteDisabled?: boolean;
  /** The section whose paste is currently in flight, for its busy state. */
  pastingEntityId?: string | null;
}

type BucketTreeProps = BucketTreeCommonProps & (BucketTreeEditProps | BucketTreeBrowseProps);

/**
 * The bucket document rendered as an indented, collapsible tree of entity
 * sections. Edit mode powers the bucket detail screen (inline property and
 * section editors); browse mode powers the form-page bucket browser (read-only
 * rows with per-value copy).
 */
export function BucketTree(props: BucketTreeProps) {
  const { document, defaultExpandedDepth = 0, stateKey } = props;
  const [collapsed, setCollapsed] = useSessionMemory(
    stateKey ? `bucket-tree:${stateKey}` : undefined,
    () => collapsedBelowDepth(document.root, defaultExpandedDepth)
  );
  const [copiedPropertyId, setCopiedPropertyId] = useState<string | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => clearTimeout(copyTimer.current ?? undefined), []);

  const parentByEntity = useMemo(() => {
    const parents = new Map<string, string>();
    visitEntities(document.root, (entity, _depth, parent) => {
      if (parent) parents.set(entity.id, parent.id);
    });
    return parents;
  }, [document]);

  // An in-flight draft must always be visible: expand the target section and
  // every collapsed ancestor above it.
  const draftEntityId =
    props.mode === 'edit'
      ? (props.propertyDraft?.entityId ?? props.sectionDraft?.parentEntityId ?? null)
      : null;
  useEffect(() => {
    if (!draftEntityId) return;
    setCollapsed((current) => {
      const next = new Set(current);
      for (
        let entityId: string | undefined = draftEntityId;
        entityId;
        entityId = parentByEntity.get(entityId)
      ) {
        next.delete(entityId);
      }
      return next.size === current.size ? current : next;
    });
  }, [draftEntityId, parentByEntity]);

  const toggle = (entityId: string) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(entityId)) {
        next.delete(entityId);
      } else {
        next.add(entityId);
      }
      return next;
    });
  };

  const copyValue = async (property: BucketProperty) => {
    if (props.mode !== 'browse') return;
    const copied = await props.onCopyValue(property);
    if (!copied) return;
    setCopiedPropertyId(property.id);
    clearTimeout(copyTimer.current ?? undefined);
    copyTimer.current = setTimeout(() => setCopiedPropertyId(null), 1600);
  };

  return (
    <SectionNode
      entity={document.root}
      isRoot
      depth={0}
      tree={{ props, collapsed, toggle, copyValue, copiedPropertyId }}
    />
  );
}

interface TreeContext {
  props: BucketTreeProps;
  collapsed: Set<string>;
  toggle: (entityId: string) => void;
  copyValue: (property: BucketProperty) => Promise<void>;
  copiedPropertyId: string | null;
}

function SectionNode({
  entity,
  isRoot,
  depth,
  ordinal,
  tree
}: {
  entity: BucketEntity;
  isRoot: boolean;
  depth: number;
  ordinal?: number;
  tree: TreeContext;
}) {
  const { props } = tree;
  // Narrow the mode union once; the closures below keep the narrowed types.
  const edit = props.mode === 'edit' ? props.edit : null;
  const browse = props.mode === 'browse' ? props : null;
  const busy = props.mode === 'edit' && Boolean(props.busy);
  const propertyDraft = props.mode === 'edit' ? (props.propertyDraft ?? null) : null;
  const sectionDraft = props.mode === 'edit' ? (props.sectionDraft ?? null) : null;

  const properties = entity.properties ?? [];
  const rootHeading = props.rootHeading === undefined ? 'Fields' : props.rootHeading;
  const heading = isRoot ? rootHeading : entityDisplayLabel(entity, ordinal);
  // The root starts expanded (it is never in the initial collapsed set) but
  // stays collapsible like any section; a headerless root defers to the
  // host's controlled collapse instead.
  const isCollapsed = isRoot
    ? heading === null
      ? Boolean(props.rootCollapsed)
      : tree.collapsed.has(entity.id)
    : tree.collapsed.has(entity.id);
  const addingHere =
    edit !== null && propertyDraft?.entityId === entity.id && propertyDraft.propertyId === null;
  const addingSectionHere = edit !== null && sectionDraft?.parentEntityId === entity.id;

  return (
    <div className={`entity-section entity-section--depth-${Math.min(depth, 3)}`}>
      {heading !== null && (
        <div className="fields-heading">
          <h2>
            <button
              type="button"
              className="entity-toggle"
              aria-expanded={!isCollapsed}
              onClick={() => tree.toggle(entity.id)}
            >
              <ChevronRightIcon className="entity-toggle__chevron" />
              {heading}
            </button>
          </h2>
          {isCollapsed && properties.length > 0 && (
            <span className="entity-count">{sectionSummary(entity)}</span>
          )}
          <span className="fields-heading__rule" />
          {!isRoot && browse?.onPasteSection && (
            <button
              type="button"
              className="field-edit-button section-paste-button"
              aria-label={`Fill this page from ${heading}`}
              aria-busy={browse.pastingEntityId === entity.id}
              disabled={Boolean(browse.pasteDisabled)}
              onClick={() => browse.onPasteSection?.(entity, heading ?? '')}
            >
              <PasteIcon />
            </button>
          )}
          {!isRoot && edit && (
            <button
              type="button"
              className="field-edit-button"
              aria-label={`Remove ${heading}`}
              disabled={busy}
              onClick={() => edit.onRemoveEntity(entity.id)}
            >
              <TrashIcon />
            </button>
          )}
        </div>
      )}

      {!isCollapsed && (
        <>
          <div className="field-list">
            {properties.map((property) =>
              edit && propertyDraft?.propertyId === property.id ? (
                <PropertyEditor
                  key={property.id}
                  draft={propertyDraft}
                  busy={busy}
                  onChange={edit.onEditProperty}
                  onSave={edit.onSaveProperty}
                  onCancel={edit.onCancelProperty}
                  onRemove={edit.onRemoveProperty}
                />
              ) : (
                <PropertyRow
                  key={property.id}
                  entityId={entity.id}
                  property={property}
                  busy={busy}
                  edit={edit}
                  copied={tree.copiedPropertyId === property.id}
                  onCopy={browse ? () => void tree.copyValue(property) : undefined}
                />
              )
            )}
          </div>

          {addingHere && propertyDraft && edit ? (
            <PropertyEditor
              draft={propertyDraft}
              busy={busy}
              onChange={edit.onEditProperty}
              onSave={edit.onSaveProperty}
              onCancel={edit.onCancelProperty}
            />
          ) : (
            edit && (
              <button
                type="button"
                className="add-field-button"
                disabled={busy}
                onClick={() =>
                  edit.onEditProperty({
                    entityId: entity.id,
                    propertyId: null,
                    semanticKey: '',
                    displayLabel: '',
                    kind: 'TEXT',
                    value: ''
                  })
                }
              >
                <PlusIcon />
                Add field {isRoot ? '' : `to ${heading}`}
              </button>
            )
          )}
        </>
      )}

      {/* Child section headers always render, so collapsing a section leaves
          the tree's outline visible instead of swallowing whole branches;
          each child still honors its own collapsed state. */}
      {childrenWithOrdinals(entity).map(({ child, ordinal: childOrdinal }) => (
        <SectionNode
          key={child.id}
          entity={child}
          isRoot={false}
          depth={depth + 1}
          ordinal={childOrdinal}
          tree={tree}
        />
      ))}

      {!isCollapsed && (
        <>
          {addingSectionHere && sectionDraft && edit ? (
            <SectionEditor entityId={entity.id} draft={sectionDraft} busy={busy} edit={edit} />
          ) : (
            edit &&
            depth < 3 && (
              <button
                type="button"
                className="add-field-button add-field-button--section"
                disabled={busy}
                onClick={() => edit.onEditSection({ parentEntityId: entity.id, label: '' })}
              >
                <PlusIcon />
                Add section {isRoot ? '' : `under ${heading}`}
              </button>
            )
          )}
        </>
      )}
    </div>
  );
}

/** One read-only property line with its edit pencil or copy affordance. */
function PropertyRow({
  entityId,
  property,
  busy,
  edit,
  copied,
  onCopy
}: {
  entityId: string;
  property: BucketProperty;
  busy: boolean;
  edit: BucketTreeEditHandlers | null;
  copied: boolean;
  onCopy?: () => void;
}) {
  const label = property.displayLabel || humanizeName(property.semanticKey);
  return (
    <div className="bucket-field">
      <span className="bucket-field__copy">
        <span>{label}</span>
        <strong title={property.value}>{property.value || 'Empty'}</strong>
      </span>
      {edit && (
        <button
          type="button"
          className="field-edit-button"
          aria-label={`Edit ${label}`}
          disabled={busy}
          onClick={() =>
            edit.onEditProperty({
              entityId,
              propertyId: property.id,
              semanticKey: property.semanticKey,
              displayLabel: label,
              kind: property.kind,
              value: property.value
            })
          }
        >
          <PencilIcon />
        </button>
      )}
      {onCopy && (
        <button
          type="button"
          className={`field-edit-button field-copy-button${
            copied ? ' field-copy-button--copied' : ''
          }`}
          aria-label={copied ? 'Copied' : `Copy ${label}`}
          disabled={property.value.trim() === ''}
          onClick={onCopy}
        >
          {copied ? <CheckIcon /> : <CopyIcon />}
        </button>
      )}
    </div>
  );
}

/** The inline editor for naming a new child section. */
function SectionEditor({
  entityId,
  draft,
  busy,
  edit
}: {
  entityId: string;
  draft: SectionDraft;
  busy: boolean;
  edit: BucketTreeEditHandlers;
}) {
  return (
    <div className="field-editor">
      <label htmlFor={`section-label-${entityId}`}>Section name</label>
      <input
        autoFocus
        id={`section-label-${entityId}`}
        value={draft.label}
        placeholder="Driver, Vehicle, Location…"
        onChange={(event) => edit.onEditSection({ ...draft, label: event.target.value })}
        onKeyDown={(event) => {
          if (event.key === 'Enter') edit.onSaveSection();
          if (event.key === 'Escape') edit.onCancelSection();
        }}
      />
      <div className="field-editor__actions">
        <span />
        <button
          type="button"
          className="button button--secondary button--pill"
          disabled={busy}
          onClick={edit.onCancelSection}
        >
          Cancel
        </button>
        <button
          type="button"
          className="button button--primary button--pill"
          disabled={busy}
          onClick={edit.onSaveSection}
        >
          Add section
        </button>
      </div>
    </div>
  );
}

/**
 * "3 fields" shown on a collapsed header. Only the section's own fields hide
 * on collapse — child sections stay visible — so the chip counts fields only.
 */
function sectionSummary(entity: BucketEntity): string {
  const fields = entity.properties?.length ?? 0;
  return `${fields} ${pluralize(fields, 'field')}`;
}

/** All entity ids strictly deeper than the given depth (root is depth 0). */
function collapsedBelowDepth(root: BucketEntity, expandedDepth: number): Set<string> {
  const collapsedIds = new Set<string>();
  visitEntities(root, (entity, depth) => {
    if (depth > expandedDepth) collapsedIds.add(entity.id);
  });
  return collapsedIds;
}

function PropertyEditor({
  draft,
  busy,
  onChange,
  onSave,
  onCancel,
  onRemove
}: {
  draft: PropertyDraft;
  busy: boolean;
  onChange: (draft: PropertyDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  onRemove?: () => void;
}) {
  const id = draft.propertyId ?? 'new';
  return (
    <div className="field-editor">
      <label htmlFor={`field-label-${id}`}>Field label</label>
      <input
        autoFocus
        id={`field-label-${id}`}
        value={draft.displayLabel}
        onChange={(event) => onChange({ ...draft, displayLabel: event.target.value })}
      />
      <label htmlFor={`field-kind-${id}`}>Type</label>
      <select
        id={`field-kind-${id}`}
        value={draft.kind}
        onChange={(event) => onChange({ ...draft, kind: event.target.value as BucketValueKind })}
      >
        {BUCKET_VALUE_KINDS.map((kind) => (
          <option key={kind} value={kind}>
            {kindLabel(kind)}
          </option>
        ))}
      </select>
      <label htmlFor={`bucket-value-${id}`}>Value</label>
      <input
        id={`bucket-value-${id}`}
        value={draft.value}
        onChange={(event) => onChange({ ...draft, value: event.target.value })}
      />
      <div className="field-editor__actions">
        {onRemove && (
          <button type="button" className="field-remove" disabled={busy} onClick={onRemove}>
            Remove
          </button>
        )}
        <span />
        <button
          type="button"
          className="button button--secondary button--pill"
          disabled={busy}
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="button button--primary button--pill"
          disabled={busy}
          onClick={onSave}
        >
          Save field
        </button>
      </div>
    </div>
  );
}

function kindLabel(kind: BucketValueKind): string {
  switch (kind) {
    case 'TEXT':
      return 'Text';
    case 'BOOLEAN':
      return 'Yes / no';
    case 'NUMBER':
      return 'Number';
    case 'DATE':
      return 'Date';
    case 'PHONE':
      return 'Phone';
    case 'EMAIL':
      return 'Email';
    case 'MONEY':
      return 'Money';
    case 'IDENTIFIER':
      return 'Identifier';
  }
}
